import React, {useContext, useEffect, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {UserContext} from '../../utils/UserContext';
import {cookieLoginApi, csrfHeader, getCsrfToken, getUser, loginApi} from '../../api/authApi';
import {getNotificationsApi} from '../../api/notificationsApi';
import {Alert, Button, Form, Input, Spin, Modal} from 'antd';
import {GoogleCircleFilled, LoadingOutlined, LockOutlined, MailOutlined} from '@ant-design/icons';
import {permColors} from '../../utils/colors';
import {GoogleAuthProvider, getAuth, signInWithPopup} from 'firebase/auth';
import axios from 'axios';
import {hrUrl, mainUrl} from '../../api/Urls';
import {useAtom} from 'jotai';
import {notificationsAtom, unreadNotificationsAtom} from '../../utils/atoms';
import {ROUTES} from '../../utils/ROUTES';
import {LoginOutlined} from '@mui/icons-material';
import {useAuthRedirect} from './redirector';
import {getCsrfTokenApi} from '../../utils/authHelpers';
import { HR_LOGIN_PARAM } from '../../pages/AuthPage/AuthPage';

export default function Login() {
	const [form] = Form.useForm();
	const [formTwoFactor] = Form.useForm();
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [googleEmailError, setGoogleEmailError] = useState('');
	const [currentUser, setCurrentUser] = useContext(UserContext);
	const [, setNotifications] = useAtom(notificationsAtom);
	const [, setUnreadNotifications] = useAtom(unreadNotificationsAtom);
	const {handleRedirect} = useAuthRedirect();
	const [twoFactorLogin, setTwoFactorLogin] = useState(false);
	const [twoFactorCode, setTwoFactorCode] = useState('');
	const [searchParams, setSearchParams] = useSearchParams();

	const handleLocalRedirect = (userData) => {
		if (searchParams.get(HR_LOGIN_PARAM)) {
			window.location.href = hrUrl;
		} else {
			handleRedirect(userData);
		}	
	}


	// function to clear all errors
	function clearErrors() {
		setError(false);
		setGoogleEmailError('');
	}

	async function getNotifications() {
		if (localStorage.getItem('accessToken')) {
			const res = await getNotificationsApi();
			console.log(res);
			setNotifications(res);
			setUnreadNotifications(res?.filter((notif) => notif.read === false).length);
			console.log('Successfully fetched notifications');
		}
	}

	const firebaseGoogleAuthApiLocal = async () => {
		const auth = getAuth();
		const provider = new GoogleAuthProvider();

		try {
			const result = await signInWithPopup(auth, provider);
			setLoading(true);
			const user = result.user;
			const idToken = await user.getIdToken();
			const csrfToken = await getCsrfTokenApi();
			const response = await axios.post(
				mainUrl + '/auth/cookie/firebase-google-auth/',
				{
					idToken: idToken,
				},
				{
					withCredentials: true,
					headers: {
						'X-CSRFToken': csrfToken,
					},
				}
			);
			console.log(response.data);
			return response.data;
		} catch (error) {
			console.error('Error during Google sign-in:', error);
			setLoading(false);
		}
	};
	async function firebaseGoogleLogin() {
		try {
			const response = await firebaseGoogleAuthApiLocal();
			console.log('response from firebaseGoogleAuthApiLocal', response);
			if (!response?.access) {
				throw new Error('Invalid response from Google authentication');
			}
			localStorage.setItem('accessToken', response.access);
			const userResponse = await getUser();
			if (!userResponse?.data) {
				throw new Error('Invalid user response');
			}
			await setCurrentUser(userResponse.data);
			await getNotifications();
			setLoading(false);
			console.log('current user redirecting to: ', currentUser);
			handleLocalRedirect(userResponse.data);
		} catch (error) {
			console.error(error);
			clearErrors();
			setGoogleEmailError(error?.response?.data?.detail || 'An error occurred during login');
			setLoading(false);
		}
	}

	function handleTwoFactorLoginCheck(values) {
		if (values?.requires_2fa) {
			setError('');
			console.log(values);
			setTwoFactorLogin(true);
			setTwoFactorCode(values?.session_token);
			formTwoFactor.resetFields(); // Reset the form when opening the modal
		}
	}

	async function handleTwoFactorLogin(values) {
		console.log(values);
		try {
			setLoading(true);
			const response = await axios.post(mainUrl + '/auth/two-factor/verify/', {
				session_token: twoFactorCode,
				verification_code: values.code,
			});
			console.log(response);
			clearErrors();
			localStorage.setItem('token', response.data.refresh);
			localStorage.setItem('accessToken', response.data.access);
			const userData = await getUser();
			await setCurrentUser(userData.data);
			await getNotifications();
			setLoading(false);
			handleLocalRedirect(userData.data);
		} catch (error) {
			console.error(error);
			setError(error?.response?.data?.error || 'Error verifying code');
			setLoading(false);
		}
	}


	async function login(values) {
		try {
			setLoading(true);
			// const {data} = await loginApi(values.email.toLowerCase(), values.password);
			const {data} = await cookieLoginApi(values.email.toLowerCase(), values.password);
			if (data?.requires_2fa) {
				handleTwoFactorLoginCheck(data);
				setLoading(false);
				return;
			}

			clearErrors();
			localStorage.setItem('accessToken', data.access);
			const userData = await getUser();
			await setCurrentUser(userData.data);
			await getNotifications();
			setLoading(false);
			handleLocalRedirect(userData.data);
		} catch (error) {
			clearErrors();
			if (error?.response?.data?.detail?.includes('not verified')) {
				setError(
					<div className="text-sm">
						Your email is not verified. Please check your email for a verification link.
						<Link to={ROUTES.CONFIRM_EMAIL} className="text-blue-500 ml-1 hover:text-blue-600">
							Resend verification email
						</Link>
					</div>
				);
			} else {
				setError(error?.response?.data?.detail || 'An error occurred during login');
			}
			setLoading(false);
		}
	}


	return (
		<div>
			<Form form={form} onFinish={login} layout="vertical">
				<div className="text">
					<h1 className="text-2xl sm:text-3xl font-medium text-darkCharcoal mb-2">Welcome back!</h1>
					<h2 className="text-base font-normal text-charcoal leading-relaxed">Sign in to continue your journey.</h2>
				</div>
				<Form.Item
					label={
						<span className="font-normal text-sm" style={{color: permColors.darkCharcoal}}>
							Email
						</span>
					}
					name="email"
					rules={[
						{required: true, message: 'Please input your email!'},
						{type: 'email', message: 'Please enter a valid email!'},
					]}>
					<Input placeholder="Enter your email" allowClear prefix={<MailOutlined className="site-form-item-icon mr-1 opacity-20" />} />
				</Form.Item>
				<Form.Item
					label={
						<span className="font-normal text-sm" style={{color: permColors.darkCharcoal}}>
							Password
						</span>
					}
					name="password"
					rules={[
						{required: true, message: 'Please input your password!'},
						{min: 6, message: 'Password must be at least 6 characters!'},
					]}>
					<Input.Password placeholder="Enter your password" allowClear prefix={<LockOutlined className="site-form-item-icon mr-1 opacity-20" />} />
				</Form.Item>
				{error && <Alert message={error} type="error" showIcon className="mb-3 flex justify-start mt-2 rounded-md" banner />}
				{googleEmailError && <Alert message={googleEmailError} type="error" showIcon className="mb-3 mt-2 rounded-md" banner />}

				<Form.Item>
					<Button htmlType="submit" className="min-h-[40px] w-full m-0" type="primary" disabled={loading} icon={<LoginOutlined className="text-base" />}>
						Log in
					</Button>
				</Form.Item>
				<div className="text-sm text-charcoal  mt-1">
					Forgot password?{' '}
					<Link to="/reset-password" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
						Reset password
					</Link>
				</div>

				{loading && (
					<Alert
						message={<span className="ml-2">Signing in</span>}
						type="info"
						icon={
							<Spin
								indicator={
									<LoadingOutlined
										style={{
											fontSize: 16,
										}}
										spin
									/>
								}
								size="small"
							/>
						}
						banner
						className="mb-3 mt-2 rounded-md"
						style={{color: permColors.deepBlue}}
					/>
				)}
			</Form>

			<Modal
				title="Two-Factor Authentication"
				open={twoFactorLogin}
				onCancel={() => setTwoFactorLogin(false)}
				maskClosable={false}
				destroyOnClose
				className="max-w-[320px]"
				footer={null}
				centered
				mask
				maskProps={{style: {backdropFilter: 'blur(6px)', background: 'rgba(0, 0, 0, 0.2)'}}}>
				<div className="text-sm text-grey mb-4 font-light">Enter the 6-digit code from your authenticator app</div>

				<Form form={formTwoFactor} onFinish={handleTwoFactorLogin} layout="vertical">
					<Form.Item label="Verification Code" name="code" rules={[{required: true, message: 'Please input your verification code!'}]} className="w-full flex justify-center items-center">
						<Input.OTP size="large" length={6} autoFocus inputType="numeric" className="w-full" style={{boxSizing: 'border-box'}} inputStyle={{width: '100%', aspectRatio: '1/1'}} />
					</Form.Item>
					{error && <Alert message={error} type="error" showIcon className="mb-3 flex justify-start mt-2 rounded-md" banner />}
					<Form.Item className="mb-0">
						<Button htmlType="submit" className="min-h-fit w-full m-0" type="primary" disabled={loading} icon={<LoginOutlined className="text-base" />}>
							Verify Code
						</Button>
					</Form.Item>
					{loading && (
						<Alert
							message={<span className="ml-2">Verifying code</span>}
							type="info"
							icon={
								<Spin
									indicator={
										<LoadingOutlined
											style={{
												fontSize: 16,
											}}
											spin
										/>
									}
									size="small"
								/>
							}
							banner
							className=" mt-2 rounded-md"
							style={{color: permColors.deepBlue}}
						/>
					)}
				</Form>
			</Modal>

			<div className="divider-container">
				<div className="divider">
					<hr />
					<span className="text-xs">OR</span>
					<hr />
				</div>
			</div>

			<Button
				onClick={firebaseGoogleLogin}
				icon={<GoogleCircleFilled className="text-2xl py-1" style={{color: '#4285F4'}} />}
				className="w-full h-fit flex items-center justify-center min-h-[40px]">
				Continue with Google
			</Button>
		</div>
	);
}
